import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  RegistrationFileRecord,
  RegistrationDocument,
  GroupedRegistrationDocumentResults,
} from '@stax-core/types-omni';

import {
  ResponsivePadding,
  Group,
  Files,
  TooltipIcon,
  FormHeader,
  InlineError,
} from '../../components';
import { coreapi, onboardingapi } from '../../api';
import { useToaster, useAuthToken, useAsyncEffect } from '../../hooks';
import { downloadData } from '../../util/data.util';
import { formatTitleCase } from '../../util/string.util';

interface DocumentsProps {
  accept?: string;
  /** When true, render an inline error under any required document that has no uploaded files */
  showRequiredErrors?: boolean;
  /** Notifies the parent of required documents that still have no uploaded files */
  onMissingRequiredChange?: (missing: RegistrationDocument[]) => void;
}

/** A required document is missing when `is_required` is true and it has no uploaded files. */
function hasUploadedFile(doc: RegistrationDocument): boolean {
  return Array.isArray(doc.files) && doc.files.length > 0;
}

function getMissingRequiredDocuments(
  grouped: GroupedRegistrationDocumentResults,
): RegistrationDocument[] {
  return Object.values(grouped)
    .flat()
    .filter((doc): doc is RegistrationDocument => !!doc)
    .filter(doc => doc.is_required && !hasUploadedFile(doc));
}

export const Documents: FunctionComponent<DocumentsProps> = props => {
  const { accept, showRequiredErrors, onMissingRequiredChange } = props;
  // TODO: add document uploads via smartphone
  const { authToken } = useAuthToken();
  const { toaster, toast } = useToaster();

  const [registrationDocuments, setRegistrationDocuments] =
    useState<GroupedRegistrationDocumentResults>({});

  const registrationDocumentSubjects = Object.keys(registrationDocuments);

  // Report required-but-not-uploaded documents up so the page can gate progression.
  useEffect(() => {
    onMissingRequiredChange?.(
      getMissingRequiredDocuments(registrationDocuments),
    );
  }, [registrationDocuments, onMissingRequiredChange]);

  useAsyncEffect(async () => {
    getRegistrationDocuments();
  }, [authToken]);

  const getRegistrationDocuments = useCallback(async () => {
    try {
      const registrationDocuments: GroupedRegistrationDocumentResults =
        await onboardingapi.get(authToken!, '/team/registration/documents');

      setRegistrationDocuments(registrationDocuments);
    } catch (error) {
      toaster(toast.error(error, 'There was a problem downloading the file.'));
    }
  }, [authToken, toaster, toast]);

  const deleteFile = useCallback(
    async (file: RegistrationFileRecord) => {
      try {
        await coreapi.delete(authToken!, `/team/registration/file/${file.id}`);
      } catch (error) {
        toaster(toast.error(error, 'There was a problem deleting the file.'));
      } finally {
        getRegistrationDocuments();
      }
    },
    [authToken, toaster, toast, getRegistrationDocuments],
  );

  const downloadFile = useCallback(
    async (file: RegistrationFileRecord) => {
      try {
        const res = await onboardingapi.get(authToken!, `/file/${file.id}`);
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        downloadData(res, file.name, file.meta?.mime!);
      } catch (error) {
        toaster(
          toast.error(error, 'There was a problem downloading the file.'),
        );
      }
    },
    [authToken, toaster, toast],
  );

  const uploadFiles = useCallback(
    async (files: File[], registrationDocumentId: string | null = null) => {
      const uploads = files.map(async file => {
        try {
          const body: { [key: string]: any } = { file };

          if (registrationDocumentId) {
            body.registration_document_id = registrationDocumentId;
          }

          await coreapi.post(authToken!, '/team/registration/file', body);
        } catch (error) {
          toaster(
            toast.error(error, 'There was a problem uploading the file.'),
          );
        }
      });

      await Promise.all(uploads);

      getRegistrationDocuments();
    },
    [authToken, toast, toaster, getRegistrationDocuments],
  );

  return (
    <ResponsivePadding desktop="0" mobile="0">
      <Group direction="column" space="50px">
        {registrationDocumentSubjects.map((subject, i) => {
          const documents: RegistrationDocument[] =
            registrationDocuments[subject];
          const id = `${subject}-uploader-group`;

          if (!documents || !documents.length || !Array.isArray(documents))
            return null;

          return (
            <Group
              direction="column"
              space="4px"
              key={id}
              id={id}
              data-testid={id}
            >
              <FormHeader
                tag="h3"
                bordered
                style={{ marginTop: 0, paddingTop: 0 }}
              >
                {formatTitleCase(subject.replace(/_/g, ' '))}
              </FormHeader>
              <ResponsivePadding desktop="0 60% 0 0" mobile="0">
                <Group
                  direction="column"
                  space="16px"
                  data-testid="columnContainer"
                >
                  {documents.map(doc => {
                    const { id, title, meta, files } = doc;

                    const tooltip =
                      meta && typeof meta === 'object' ? meta.tooltip : '';

                    return (
                      <Group
                        direction="column"
                        space="4px"
                        key={id}
                        id={`${id}-uploader`}
                        data-testid={`${id}-uploader`}
                      >
                        <label>
                          {title}{' '}
                          {tooltip ? <TooltipIcon tooltip={tooltip} /> : null}
                        </label>
                        <Files
                          accept={accept}
                          files={files}
                          onDrop={files => uploadFiles(files, id)}
                          onDownload={downloadFile}
                          onDelete={deleteFile}
                        />
                        {showRequiredErrors &&
                        doc.is_required &&
                        !hasUploadedFile(doc) ? (
                          <InlineError
                            id={`${id}-required-error`}
                            data-testid={`${id}-required-error`}
                          >
                            Please upload {title || 'this document'}
                          </InlineError>
                        ) : null}
                      </Group>
                    );
                  })}
                </Group>
              </ResponsivePadding>
            </Group>
          );
        })}
      </Group>
    </ResponsivePadding>
  );
};
